/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Warren Dowey
 */

import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { includes } from 'lodash-es';

import { Logger } from '@app/services/logger/logger.service';
import enUS from '@trans/en-US.json';
import frFR from '@trans/fr-FR.json';
import { Subscription } from 'rxjs';

const log = new Logger('i18nService');
const languageKey = 'language';

/**
 *  Warren Dowey
 *
 *  extract
 *
 *  Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 *
 * @param stringToExtract The string to extract for translation.
 *
 * @return The same string.
 */
export function extract(stringToExtract: string) {
  return stringToExtract;
}

@Injectable()
export class I18nService implements OnDestroy {
  defaultLanguage: string;
  supportedLanguages: string[];

  // Subscriptions
  private translateSubscription: Subscription;

  constructor(private translateService: TranslateService) {
    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en-US', enUS);
    translateService.setTranslation('fr-FR', frFR);
  }
  /**
   *  Warren Dowey
   *
   *  extract
   *
   *  Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   *
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   *
   * @return void initlaises i18n
   */
  init(defaultLanguage: string, supportedLanguages: string[]) {
    this.defaultLanguage = defaultLanguage;
    this.supportedLanguages = supportedLanguages;
    this.language = '';
    this.translateSubscription = this.translateService.onLangChange.subscribe(
      (event: LangChangeEvent) => {
        localStorage.setItem(languageKey, event.lang);
        if (this.translateSubscription) {
          this.translateSubscription.unsubscribe();
        }
      }
    );
  }
  /**
   *  Warren Dowey
   *
   *  setlanguage
   *
   *  Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present). *
   *
   * @param language The IETF language code to set.
   *
   * @return void sets the current language
   */
  set language(language: string) {
    language =
      language ||
      localStorage.getItem(languageKey) ||
      this.translateService.getBrowserCultureLang();
    let isSupportedLanguage = includes(this.supportedLanguages, language);
    // If no exact match is found, search without the region
    if (language && !isSupportedLanguage) {
      language = language.split('-')[0];
      language =
        this.supportedLanguages.find((supportedLanguage) =>
          supportedLanguage.startsWith(language)
        ) || '';
      isSupportedLanguage = Boolean(language);
    }
    // Fallback if language is not supported
    if (!isSupportedLanguage) {
      language = this.defaultLanguage;
    }
    log.debug(`Language set to ${language}`);
    this.translateService.use(language);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): string {
    return this.translateService.currentLang;
  }

  ngOnDestroy() {
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }
}
